import { useState, useCallback } from 'react';
import type { KnowledgeEntry, ConfidenceStats } from '../types/foreman';
import { buildChatSend } from '../gateway/protocol';
import { gateway } from '../gateway/connection';

interface UseKnowledgeProps {
  send: (data: unknown) => void;
  activeSessionId: string | null;
}

export function useKnowledge({ send, activeSessionId }: UseKnowledgeProps = { send: () => {}, activeSessionId: null }) {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [stats, setStats] = useState<ConfidenceStats | null>(null);
  const [searchResults, setSearchResults] = useState<KnowledgeEntry[]>([]);
  const [teachMode, setTeachMode] = useState(false);

  const addEntry = useCallback((entry: KnowledgeEntry) => {
    setEntries((prev) => [entry, ...prev]);
  }, []);

  const searchKnowledge = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    setSearchResults(
      entries.filter(
        (entry) =>
          entry.title.toLowerCase().includes(lowerQuery) ||
          entry.description.toLowerCase().includes(lowerQuery) ||
          entry.source.toLowerCase().includes(lowerQuery) ||
          entry.type.toLowerCase().includes(lowerQuery)
      )
    );
  }, [entries]);

  const toggleTeachMode = useCallback(() => {
    setTeachMode((prev) => !prev);
  }, []);

  const submitCorrection = useCallback(
    (
      messageId: string,
      correction: string,
      category: string
    ) => {
      // Send the correction to the gateway, which routes it to the foreman-learning skill.
      // The message is prefixed with [CORRECTION] so the backend can detect and process it.
      const correctionPayload = `[CORRECTION] messageId=${messageId} category=${category} :: ${correction}`;
      const params = buildChatSend(activeSessionId || 'main', correctionPayload);
      gateway.request('chat.send', params).catch(console.error);

      // Also add to local state so the UI reflects the correction immediately
      const entry: KnowledgeEntry = {
        id: Date.now(),
        type: 'correction',
        title: 'Correction submitted',
        description: correction,
        source: 'User correction',
        timestamp: new Date().toISOString(),
        appliedTo: [],
      };
      addEntry(entry);
    },
    [addEntry, send, activeSessionId]
  );

  const refreshStats = useCallback(() => {
    const token = localStorage.getItem('foreman-auth-token');
    fetch('/api/training/stats', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch stats');
        return res.json();
      })
      .then((data: ConfidenceStats) => {
        setStats(data);
      })
      .catch(() => {
        // Fallback to empty stats on error
        setStats({
          overall: 0,
          bySkill: {},
          byCategory: {},
          recentCorrections: 0,
          knowledgeEntries: 0,
          patternDbJobs: 0,
          lowConfidenceAreas: [],
        });
      });
  }, []);

  return {
    entries,
    stats,
    searchResults,
    teachMode,
    addEntry,
    searchKnowledge,
    toggleTeachMode,
    submitCorrection,
    refreshStats,
  };
}
